import * as React from "react";

import { cn } from "../../lib/utils";

/* The Sirdab card: a labelled section of a form or detail page, never a
   free-floating panel. Trued after the shadcn add: no shadow, no py-6/gap-6
   spacing scale, and rounded-lg rather than shadcn's default rounded-xl,
   because that is the box every dashboard screen already hand-rolled as
   `rounded-lg border border-border bg-card p-4`: 14 identical copies
   across the orders/products/home/customers/discounts ports. This is that
   shape, named once.

   CARD TONE: a card that REPORTS a state takes `tone` (muted, warning).
   Muted is the tonal panel, the quiet band for a notice that is context
   rather than content (a closed month, a snapshot stamp); warning is the
   warning-subtle banner pair, foreground text on the tint, for a notice
   the reader must weigh before acting. Status reports, it never commands,
   so neither tone touches the border or the text colour. The card stamps
   data-tone for the same reason TableRow does. Callers never colour a
   card through className; tone is the one door. */

type CardTone = "default" | "muted" | "warning";

const TONE_FILL: Record<CardTone, string> = {
  default: "bg-card",
  muted: "bg-muted",
  warning: "bg-warning-subtle",
};

function Card({
  tone = "default",
  className,
  ...props
}: React.ComponentProps<"div"> & {
  /** What the card REPORTS: muted is the tonal notice band, warning the
        warning-subtle pair. The border and the text stay as they are. */
  tone?: CardTone;
}) {
  return (
    <div
      data-slot="card"
      data-tone={tone === "default" ? undefined : tone}
      className={cn(
        "border-border rounded-lg border p-4",
        TONE_FILL[tone],
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn("flex flex-col gap-1", className)}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"h2">) {
  return (
    <h2
      data-slot="card-title"
      className={cn("text-heading-xs", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="card-description"
      className={cn("text-body-quiet text-muted-foreground", className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("mt-3", className)}
      {...props}
    />
  );
}

export { Card, CardHeader, CardTitle, CardDescription, CardContent };
